import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { currentLocale, convertFromBase, getActiveBaseCurrency } from '../locale'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format a numeric amount as a localised currency string.
 * - `n` is assumed to be in the active base currency (default: EUR, or the
 *   currently mounted decision's base_currency if any page has called
 *   `setActiveBaseCurrency()`).
 * - When the user has picked a locale whose currency differs from the base,
 *   the value is FX-converted using the cached daily rates.
 * - Pass `convert: false` if the value is already in the target currency.
 */
export function formatCurrency(
  n: number,
  opts?: { compact?: boolean; baseCurrency?: string; convert?: boolean },
) {
  const loc = currentLocale()
  const symbol =
    (0).toLocaleString(loc.bcp47, { style: 'currency', currency: loc.currency })
      .replace(/[\d\s.,]/g, '') || loc.currency
  if (!isFinite(n)) return `${symbol}—`
  const base = opts?.baseCurrency ?? getActiveBaseCurrency()
  const value = opts?.convert === false ? n : convertFromBase(n, base)
  if (opts?.compact) {
    return new Intl.NumberFormat(loc.bcp47, {
      style: 'currency',
      currency: loc.currency,
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value)
  }
  return new Intl.NumberFormat(loc.bcp47, {
    style: 'currency',
    currency: loc.currency,
    maximumFractionDigits: 0,
  }).format(value)
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  EUR: '€', GBP: '£', CHF: 'CHF', NOK: 'kr', SEK: 'kr', USD: '$',
}

/**
 * Rewrite the EUR symbol inside a variable-unit string to the user's active currency.
 * E.g. `€/year` → `£/year` when locale is GBP_UK.
 * Leaves non-currency units (pts/10, score, chance/year) untouched.
 */
export function localizeUnits(units: string | undefined | null): string {
  if (!units) return ''
  const loc = currentLocale()
  const sym = CURRENCY_SYMBOLS[loc.currency] || loc.currency
  if (sym === '€') return units
  return units
    .replace(/€/g, sym)
    .replace(/\bEUR\b/g, loc.currency)
}

/** The builder's risk-style names for the risk_aversion slider (0–0.01). */
export function riskStyleLabel(riskAversion: number): string {
  return riskAversion === 0 ? 'Daredevil'
    : riskAversion < 0.003 ? 'Balanced'
    : riskAversion < 0.007 ? 'Cautious' : 'Safe-as-houses'
}

export function pct(n: number, digits = 0) {
  if (!isFinite(n)) return '—'
  return `${(n * 100).toFixed(digits)}%`
}

export function quantile(arr: number[], q: number) {
  if (!arr.length) return 0
  const sorted = [...arr].sort((a, b) => a - b)
  const pos = (sorted.length - 1) * q
  const base = Math.floor(pos)
  const rest = pos - base
  return sorted[base + 1] !== undefined ? sorted[base] + rest * (sorted[base + 1] - sorted[base]) : sorted[base]
}

export function mean(arr: number[]) {
  if (!arr.length) return 0
  return arr.reduce((a, b) => a + b, 0) / arr.length
}

export function std(arr: number[]) {
  const m = mean(arr)
  return Math.sqrt(arr.reduce((a, b) => a + (b - m) ** 2, 0) / Math.max(1, arr.length))
}

/**
 * Coefficient of variation as a formatted percentage: std / |mean| × 100.
 * Returns "—" when mean is near zero (avoids division by near-zero producing
 * meaningless 4000% values).
 */
export function cvPct(stdVal: number, meanVal: number): string {
  const absMean = Math.abs(meanVal)
  if (absMean < stdVal * 0.05 || absMean < 1) return '—'
  return `${Math.round((stdVal / absMean) * 100)}%`
}

export function daysAgo(iso?: string | null): number | null {
  if (!iso) return null
  const t = new Date(iso).getTime()
  if (!isFinite(t)) return null
  return Math.floor((Date.now() - t) / 86_400_000)
}

/**
 * Coefficient of variation for a variable's distribution (σ/|μ| or analogue).
 * Higher = more uncertain. Returns -Infinity if the variable lacks a usable distribution.
 */
function _uncertaintyScore(dist?: { kind: string; params: Record<string, number> }): number {
  if (!dist || !dist.params) return -Infinity
  const p = dist.params
  switch (dist.kind) {
    case 'Normal':
    case 'StudentT':
      return p.sigma / Math.max(1, Math.abs(p.mu))
    case 'LogNormal':
      // shape param σ on log-scale → CV ≈ sqrt(exp(σ²)-1)
      return Math.sqrt(Math.max(0, Math.exp((p.sigma || 0) ** 2) - 1))
    case 'Beta': {
      const a = Math.max(1e-3, p.alpha || 0)
      const b = Math.max(1e-3, p.beta || 0)
      // CV of a Beta = sqrt( b / (a*(a+b+1)) )
      return Math.sqrt(b / (a * (a + b + 1)))
    }
    case 'Bernoulli': {
      const prob = Math.min(0.999, Math.max(0.001, p.p || 0))
      return prob * (1 - prob) * 4 // max 1 at p=0.5 — peak entropy
    }
    case 'Uniform': {
      const a = p.a || 0
      const b = p.b || 0
      const span = Math.abs(b - a)
      const mid = Math.max(1, Math.abs((a + b) / 2))
      return span / (2 * mid)
    }
    default:
      return 0
  }
}

/**
 * Pick the single variable across all of a decision's choices whose distribution has the
 * highest coefficient of variation. That's the variable most worth re-rolling / Bayesian-updating
 * because it carries the most uncertainty about your outcome.
 */
export function mostUncertainVariable(
  decision: { choices: Array<{ name: string; variables: Array<{ name: string; dist?: any }> }> },
): { name: string; choice: string } | null {
  let best: { score: number; name: string; choice: string } | null = null
  for (const c of decision.choices || []) {
    for (const v of c.variables || []) {
      const s = _uncertaintyScore(v.dist)
      if (!isFinite(s)) continue
      if (!best || s > best.score) best = { score: s, name: v.name, choice: c.name }
    }
  }
  return best ? { name: best.name, choice: best.choice } : null
}
